import { useMemo, useState } from 'react';

import { Box, Typography, Skeleton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey } from '@mui/material/colors';
import { useQuery } from '@tanstack/react-query';
import { AvailabilityDayOfWeekInterval, AvailabilityTimeOfDayInterval, getMatches, GetMatchesInput } from '@uplift/api';
import { ProviderList } from '@uplift/pages';
import { ProviderMedicalRole } from '@uplift/types';
import { ScrollableView } from '@uplift/ui-v2';

import { CrisisHotlineFooter } from '../../components';
import {
  MatchesSelectProviderData, MatchesUpdateFiltersData
} from '../../onboarding/default/types';
import { SELF_PAY_ID } from '../../onboarding/utils';
import { MatchingFiltersV2 } from './matching-filters-v2';

const EATING_DISORDERS = '26e1641d-9334-4e3a-b578-b4bead3891b7';
const SUBSTANCE_ABUSE = '15ade19a-e1a6-4fa5-94a1-24fe5a5b2f73';

export type MatchesProps = {
  onSelectProvider: (data: MatchesSelectProviderData) => void;
  onUpdateFilters: (data: MatchesUpdateFiltersData) => void;
  matchingData: {
    seed: number;
    stateId: string;
    insuranceId: string;
    medicalRole: ProviderMedicalRole;
    ageId: string | undefined;
    modalityId: string;
    specialtyIds: string[];
    identityFilters: {
      genderIds: string[];
      faithIds: string[];
      ethnicityIds: string[];
    };
    experienceFilters: {
      communityIds: string[];
      faithIds: string[];
      ethnicityIds: string[];
    };
    availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
    availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];
  };
  isBookingBoth?: boolean;
  isBookingSponsored?: boolean;
  isStudentSecondBooking?: boolean;
};

export const Matches: React.VFC<MatchesProps> = ({
  matchingData,
  onSelectProvider,
  onUpdateFilters,
  isBookingBoth,
  isBookingSponsored = false,
  isStudentSecondBooking = false
}) => {
  const restrictFiltersForPsych = matchingData.medicalRole === ProviderMedicalRole.psychiatrist;

  const [ visibleResultsCount, setVisibleResultsCount ] = useState(5);
  const { classes, cx } = useMatchesStyles();


  const matchingQueryInput: GetMatchesInput = {
    seed: matchingData.seed ?? 0.3,
    stateId: matchingData.stateId,
    medicalRole: matchingData.medicalRole,
    ageGroupId: matchingData.ageId,
    modalityId: matchingData.modalityId,
    insuranceCompanyId: matchingData.insuranceId === SELF_PAY_ID ? null : matchingData.insuranceId,
    visitReasonIds: restrictFiltersForPsych ? undefined : matchingData.specialtyIds,
    genderIds: restrictFiltersForPsych ? undefined : matchingData.identityFilters.genderIds,
    faithIds: restrictFiltersForPsych ? undefined : matchingData.identityFilters.faithIds,
    ethnicityIds: restrictFiltersForPsych ? undefined : matchingData.identityFilters.ethnicityIds,
    communityExpertise: restrictFiltersForPsych ? undefined : {
      faithIds: matchingData.experienceFilters.faithIds,
      ethnicityIds: matchingData.experienceFilters.ethnicityIds,
      generalIds: matchingData.experienceFilters.communityIds,
    },
    availableWeekdays: matchingData.availabilityDayOfWeek,
    availableDayIntervals: matchingData.availabilityTimeOfDay,
  };

  const { data, isLoading } = useQuery({
    queryKey: [ 'get-matches', matchingQueryInput ],
    queryFn: () => getMatches(matchingQueryInput),
    select: data => data.data,
  });

  const matchingProfiles = useMemo(() => data?.matchingProfiles ?? [], [ data?.matchingProfiles ]);

  const missingExperienceFilters = useMemo(() => {
    const { genderIds, faithIds, ethnicityIds } = matchingData.identityFilters;
    const wanted = genderIds.concat(faithIds).concat(ethnicityIds);

    const profileGenderIds = matchingProfiles.flatMap(x => x.genders.flatMap(y => y.genderId));
    const profileFaithIds = matchingProfiles.flatMap(x => x.faiths.flatMap(y => y.faithId));
    const profileEthnicityIds = matchingProfiles.flatMap(x => x.ethnicities.flatMap(y => y.ethnicityId));

    const providersHave = profileGenderIds.concat(profileFaithIds).concat(profileEthnicityIds);

    return wanted.filter(x => !providersHave.includes(x));
  }, [ matchingData.identityFilters, matchingProfiles ]);

  const header = useMemo(() => {
    if (matchingProfiles.length > 0 && (!data?.noInsuranceProviders || isBookingSponsored) && !missingExperienceFilters.length) {
      return (
        <Box className={classes.matchingHeader}>
          <Typography variant="h1">
            We found&nbsp;
            <span className={cx(classes.countContainer, isLoading && classes.countContainerLoading)}>
              {matchingProfiles.length}&nbsp;{isLoading && <Skeleton variant="text" className={classes.countSkeleton} />}
            </span>
            {matchingData.medicalRole === ProviderMedicalRole.psychiatrist ? 'provider' : 'therapist'}{matchingProfiles.length === 1 ? '' : 's'}&nbsp;
            <Typography variant="inherit" color="secondary">for you</Typography>
          </Typography>
        </Box>
      );
    }

    return null;
  }, [ classes.countContainer, classes.countContainerLoading, classes.countSkeleton, classes.matchingHeader, data?.noInsuranceProviders, isBookingSponsored, isLoading, matchingData.medicalRole, matchingProfiles.length, missingExperienceFilters.length ]);

  return (
    <ScrollableView className={classes.bg}>
      <div className={classes.filterContainer}>
        <MatchingFiltersV2
          matchingData={matchingData}
          onUpdateFilters={onUpdateFilters}
          className={classes.filters}
          isBookingBoth={isBookingBoth}
          isBookingSponsored={isBookingSponsored}
          isStudentSecondBooking={isStudentSecondBooking}
        />
      </div>
      <div className={classes.root}>
        {header}
        <ProviderList
          isLoading={isLoading}
          medicalRole={matchingData.medicalRole}
          matchingProfiles={matchingProfiles}
          insuranceCompanyId={matchingData.insuranceId}
          providersToShow={visibleResultsCount}
          onLoadMore={() => setVisibleResultsCount(matchingProfiles.length)}
          onViewClick={({ user }) => onSelectProvider({ providerId: user.id, matchingPaymentType: data.type })}
          stateId={matchingData.stateId}
          matchingPaymentType={data?.type}
          noInsuranceProviders={data?.noInsuranceProviders}
          emptyListRelaxed={data?.emptyListRelaxed}
          isBookingSponsored={isBookingSponsored}
          specialtyMatches={[
            matchingData.specialtyIds.includes(EATING_DISORDERS) ? EATING_DISORDERS : undefined,
            matchingData.specialtyIds.includes(SUBSTANCE_ABUSE) ? SUBSTANCE_ABUSE : undefined
          ].filter(x => !!x)}
          missingExperienceFilters={missingExperienceFilters}
        />

      </div>
      <CrisisHotlineFooter pb={1} />
    </ScrollableView>
  );
};

const useMatchesStyles = makeStyles({ name: 'Matches' })(theme => ({
  root: {
    boxSizing: 'border-box',
    margin: '0 auto',
    maxWidth: '792px',
    width: '100%',
    flex: 1,
  },
  matchingHeader: {
    paddingTop: theme.spacing(3),

    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2)
    }
  },
  filterContainer: {
    background: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2)
  },
  filters: {
    boxSizing: 'border-box',
    margin: '0 auto',
    maxWidth: '792px',
    width: '100%',
    flex: 1,
  },
  countContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  countContainerLoading: {
    visibility: 'hidden',
  },
  countSkeleton: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    visibility: 'visible',
  },
  noProvidersContainer: {
    marginTop: theme.spacing(4),
    boxSizing: 'content-box',
  },
  bg: {
    backgroundColor: grey[50]
  }
}));
